import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Avatar } from '@/components/atoms/Avatar/Avatar/Avatar';
import { DeliveryErrorTooltip } from '@/components/atoms/DeliveryErrorTooltip/DeliveryErrorTooltip';
import { useMarkdownToHtml } from '@/hooks/useMarkdownToHtml';
import { cn } from '@/lib/utils';
import { Attachment } from '@/modules/humanChat/components/Conversation/MessageArea/Attachment/Attachment';
import Attachments from '@/modules/humanChat/components/Conversation/MessageArea/Attachment/Attachments';
import { NameAvatar } from '@/modules/humanChat/components/Conversation/MessageArea/ConversationActivity/NameAvatar';
import { selectSessionSource } from '@/modules/tryIt/redux/selectors';
import { BroadcastAction } from '@/shared/types/broadcast';
import { ConversationSource } from '@/shared/types/chat';
import { splitArray } from '@/util/util';

interface AttachmentType {
  id?: string;
  url: string;
  type: 'audio' | 'video' | 'image' | 'file';
  name?: string;
  size?: number;
  width?: number;
  height?: number;
  // Using unknown instead of any to satisfy ESLint
  [key: string]: string | number | boolean | undefined;
}

interface Props {
  text: string;
  author_type: string;
  author_id: string;
  author_name?: string;
  template?: BroadcastAction;
  body: Record<string, string | AttachmentType[] | unknown>;
  turnRow?: boolean;
  hideAuthor?: boolean;
}

interface OptionType {
  text: string;
  label: string;
}

const MessageSender = ({ authorType, authorId }: { authorType: string; authorId: string }) => {
  if (authorType === 'agent') {
    return <Avatar size="medium" userId={authorId} />;
  }

  return null;
};

export const FailureLabel = ({
  errorCode,
  className,
}: {
  errorCode: string;
  className?: string;
}) => {
  const { t } = useTranslation();

  return (
    <DeliveryErrorTooltip errorCode={errorCode} side="top">
      <span className={cn('w-fit text-sm leading-5 font-normal text-destructive', className)}>
        {t('conversation.message_failed')}
      </span>
    </DeliveryErrorTooltip>
  );
};

const MessageAttachments = ({
  attachments,
  hasText,
  source,
}: {
  attachments: AttachmentType[];
  hasText: boolean;
  source?: ConversationSource;
}) => {
  if (!attachments.length) {
    return null;
  }

  return (
    <div className={cn({ 'mb-2': hasText })}>
      {splitArray(attachments, 4).map((chunk, chunkIndex) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={chunkIndex}>
          <Attachments>
            {chunk.map((attachment) => (
              <Attachment
                key={attachment?.id ?? attachment.url}
                attachment={attachment}
                source={source}
                isSingleItem={attachments.length === 1}
                bg="light"
                authorType="agent"
              />
            ))}
          </Attachments>
        </div>
      ))}
    </div>
  );
};

const MessageOptions = ({ options }: { options?: OptionType[] }) => {
  if (!options?.length) {
    return null;
  }

  return (
    <div className="my-2 mr-9 flex flex-wrap justify-end gap-2 text-right">
      {options.map((option) => (
        <button
          key={`option-${option.text}`}
          className="rounded-lg bg-bg-muted px-3 py-2 text-sm transition-all"
          onClick={() => {}}
          type="button"
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};

const AudioMessage = ({ url }: { url: string }) => <audio src={url} controls />;

const TextMessage = ({
  html,
  attachments,
  hasText,
  source,
  turnRow = false,
}: {
  html: string;
  attachments: AttachmentType[];
  hasText: boolean;
  source?: ConversationSource;
  turnRow?: boolean;
}) => (
  <div
    className={cn(
      'rounded-lg break-words text-foreground [&_a]:text-informational [&_a]:underline [&_a:hover]:text-accent [&_ol]:my-0 [&_ol]:flex [&_ol]:list-decimal [&_ol]:flex-col [&_ol]:gap-2 [&_ol]:pl-5 [&_p]:mb-1 [&_p]:text-sm [&_p]:leading-5 [&_p]:font-normal [&_p:last-child]:mb-0 [&_pre]:overflow-scroll [&_ul]:my-0 [&_ul]:flex [&_ul]:list-disc [&_ul]:flex-col [&_ul]:gap-2 [&_ul]:pl-5',
      turnRow
        ? 'turn-card w-full rounded-tl-sm border border-stroke bg-bg-muted-50 px-3 pt-3 pb-4 group-hover:bg-background'
        : 'my-2 ml-2 w-fit max-w-96 bg-background px-3 py-2 shadow-[0_1px_0_rgba(0,0,0,0.05),0_0_8px_rgba(0,0,0,0.03),0_30px_30px_rgba(0,0,0,0.02)]'
    )}
  >
    <MessageAttachments attachments={attachments} hasText={hasText} source={source} />

    {html && (
      <p className="text-sm leading-5 font-normal" dangerouslySetInnerHTML={{ __html: html }} />
    )}
  </div>
);

export const AgentMessage = ({
  text,
  template,
  author_type,
  author_id,
  author_name,
  body,
  turnRow = false,
  hideAuthor = false,
}: Props) => {
  const source = useSelector(selectSessionSource);
  const { html } = useMarkdownToHtml(text);
  const errorCode = body?.error_code as string | undefined;

  // Extract button options from template if available
  const options = template?.components
    ?.find((component) => component.type.toLowerCase() === 'buttons')
    ?.buttons?.map((option) => ({ text: option.text, label: option.text }));

  const attachments = (
    Array.isArray(body?.attachments) ? body?.attachments : []
  ) as AttachmentType[];

  if (body?.type === 'audio') {
    return <AudioMessage url={body.url as string} />;
  }

  if (turnRow) {
    return (
      <>
        {author_type === 'agent' && !hideAuthor && (
          <NameAvatar id={author_id} name={author_name} className="text-xs leading-4 font-normal" />
        )}
        <TextMessage
          html={html}
          attachments={attachments}
          hasText={!!text}
          source={source}
          turnRow
        />
        <MessageOptions options={options} />
      </>
    );
  }

  return (
    <>
      <div className="flex items-center">
        <MessageSender authorType={author_type} authorId={author_id} />

        <div className="mr-auto flex flex-col overflow-x-hidden">
          <TextMessage html={html} attachments={attachments} hasText={!!text} source={source} />
          {errorCode && <FailureLabel errorCode={errorCode} className="mb-2 ml-2" />}
        </div>
      </div>

      <MessageOptions options={options} />
    </>
  );
};

export default AgentMessage;
